/* ProcessPlantD2View.jsx — Wave CD · Stage D.2 (Plot Plan & Equipment
   Layout — engineering-grade refinement of A.6).

   Tabs:
     1. Overall Plot Plan       — D.2.1 SVG (large A1 sheet)
     2. Equipment Layout         — D.2.2 SVG (with spacing circles)
     3. Pipe Rack Layout         — D.2.3 SVG (rack schedule)
     4. 3D Plant                 — Babylon scene built from D.2 equipment positions
     5. 3D + Real World          — Cesium photoreal tiles + Babylon overlay
     6. Engineering Reports      — D.2.x-RPT markdown bundle

   The 3D + Cesium tabs reuse the engine from `ProcessPlantA6View.jsx`
   so the visual quality and behaviour are identical to A.6 — but the
   equipment / plot dimensions come from the D.2 specialist's
   precomputed payload (which itself derives from C.1 / B.4 / B.1).
*/
import React, { useEffect, useMemo, useState, useRef } from "react";
import { Map as MapIcon, Box, Globe, FileText, Wrench, Layers } from "lucide-react";
import api from "../../lib/api";
import {
  Plant3DTab, Real3DTab,
} from "./ProcessPlantA6View";


const TABS = [
  { id: "plot",    label: "Plot Plan",         icon: MapIcon },
  { id: "equip",   label: "Equipment Layout",  icon: Layers },
  { id: "rack",    label: "Pipe Rack Layout",  icon: Wrench },
  { id: "plant3d", label: "3D Plant",          icon: Box },
  { id: "real3d",  label: "3D + Real World",   icon: Globe },
  { id: "reports", label: "Engineering Reports", icon: FileText },
];


export default function ProcessPlantD2View({ projectId }) {
  const [delivs, setDelivs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("plot");
  const [error, setError] = useState(null);

  // Fetch D.2 deliverables + project (used for 3D geometry construction)
  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    setLoading(true);
    api.get(`/projects/${projectId}/stages/D.2/deliverables`)
      .then(r => {
        if (!alive) return;
        const items = Array.isArray(r.data) ? r.data
                      : (r.data?.deliverables || []);
        setDelivs(items);
        setError(items.length ? null : "Run Stage D.2 first to generate deliverables.");
      })
      .catch(e => {
        if (!alive) return;
        setError(e?.response?.data?.detail || e?.message || "Failed to load D.2 deliverables");
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [projectId]);

  // Listen for "deliverable-ai-done" to refresh
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.stageCode?.startsWith("D.2")) {
        api.get(`/projects/${projectId}/stages/D.2/deliverables`)
          .then(r => setDelivs(Array.isArray(r.data) ? r.data : r.data?.deliverables || []));
      }
    };
    window.addEventListener("deliverable-ai-done", handler);
    return () => window.removeEventListener("deliverable-ai-done", handler);
  }, [projectId]);

  const delivByCode = useMemo(() => {
    const m = {};
    for (const d of delivs) m[d.code] = d;
    return m;
  }, [delivs]);

  // Build geometry_3d for the Babylon scene from D.2 precomputed payload
  const geometry3d = useMemo(() => {
    const plotDeliv = delivByCode["D.2.1"];
    if (!plotDeliv) return null;
    const pc = plotDeliv.precomputed || {};
    const plotW = pc.plot_width_m || 200;
    const plotH = pc.plot_height_m || 120;
    // Pull equipment from any of: precomputed.equipment, D.2.2's register,
    // or precomputed.violation_details (which lists tagged equipment).
    let equipment = pc.equipment || [];
    if (!equipment.length && delivByCode["D.2.2"]?.precomputed?.equipment) {
      equipment = delivByCode["D.2.2"].precomputed.equipment;
    }
    // Map to Babylon scene format: x is horizontal, z is depth, centered.
    const eq3d = equipment.map(e => ({
      tag: e.tag || e.Tag || "EQ",
      name: e.name || e.service || e.tag,
      equip_type: (e.type || "vessel").toLowerCase(),
      // Translate plot coordinates (origin top-left) → Babylon (centered, +x right, +z forward)
      x: (e.x || 0) - plotW / 2,
      z: -((e.y || 0) - plotH / 2),
      diameter_m: e.d || e.diameter_m || 3,
      height_m: e.height_m || e.h || 6,
      zone: e.zone || "process",
    }));
    // Synthesise a couple of pipe racks down the middle (mirrors D.2.3 SVG)
    const pipeRacks = [
      {
        polyline: [
          [-plotW / 2 + 10, 4, 0],
          [plotW / 2 - 10, 4, 0],
        ],
        width_m: 8,
        levels: 3,
      },
    ];
    // Simple 4-quadrant zones
    const zones = [
      { name: "process",   x: 0, z: 0,
        width_m: plotW * 0.5, height_m: plotH * 0.6 },
      { name: "utility",   x: -plotW * 0.3, z: plotH * 0.3,
        width_m: plotW * 0.35, height_m: plotH * 0.3 },
      { name: "storage",   x: plotW * 0.3, z: plotH * 0.3,
        width_m: plotW * 0.35, height_m: plotH * 0.3 },
    ];
    return {
      plot_width_m: plotW,
      plot_height_m: plotH,
      equipment: eq3d,
      pipe_racks: pipeRacks,
      zones: zones,
      roads: [],
    };
  }, [delivByCode]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center",
                     color: "var(--pf-text-2)" }}>
        Loading D.2 deliverables…
      </div>
    );
  }

  return (
    <div data-testid="process-plant-d2" style={{
      display: "flex", flexDirection: "column", height: "100%",
      background: "var(--pf-bg)", color: "var(--pf-text)",
    }}>
      {/* Tabs */}
      <div style={{ display: "flex", gap: 0,
                     borderBottom: "1px solid var(--pf-border)",
                     background: "var(--pf-surface)",
                     overflowX: "auto" }}>
        {TABS.map(t => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
                    data-testid={`d2-tab-${t.id}`}
                    style={{
                      padding: "10px 20px", fontSize: 12,
                      background: active ? "var(--pf-surface-2)" : "transparent",
                      color: active ? "var(--pf-text)" : "var(--pf-text-2)",
                      border: "none",
                      borderBottom: active
                        ? "2px solid var(--pf-spark)"
                        : "2px solid transparent",
                      cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 6,
                      whiteSpace: "nowrap",
                      fontWeight: active ? 600 : 400,
                    }}>
              <Icon size={14}/>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: "auto", padding: 14 }}>
        {error && (
          <div data-testid="d2-error" style={{
            padding: 14, background: "rgba(245, 158, 11, 0.10)",
            border: "1px solid #f59e0b", borderRadius: 6,
            color: "var(--pf-text)", marginBottom: 14,
          }}>
            {error} <br/>
            <em style={{ fontSize: 12, opacity: 0.8 }}>
              Click <strong>Run Stage</strong> at the top to populate D.2.
            </em>
          </div>
        )}

        {activeTab === "plot" && (
          <D2SvgViewer deliv={delivByCode["D.2.1"]}
                       title="D.2.1 — Plot Plan (Overall)"
                       testId="d2-plot-svg-host" />
        )}
        {activeTab === "equip" && (
          <D2SvgViewer deliv={delivByCode["D.2.2"]}
                       title="D.2.2 — Equipment Layout (with spacing circles)"
                       testId="d2-equip-svg-host" />
        )}
        {activeTab === "rack" && (
          <D2SvgViewer deliv={delivByCode["D.2.3"]}
                       title="D.2.3 — Pipe Rack Layout"
                       testId="d2-rack-svg-host" />
        )}

        {activeTab === "plant3d" && (
          geometry3d ? (
            <Plant3DTab geometry={geometry3d} />
          ) : (
            <D2EmptyHint message="Run D.2 to populate the 3D plant model." />
          )
        )}
        {activeTab === "real3d" && (
          geometry3d ? (
            <Real3DTab geometry={geometry3d} geojson={null} />
          ) : (
            <D2EmptyHint message="Run D.2 to populate the 3D + Real World view." />
          )
        )}

        {activeTab === "reports" && (
          <D2ReportsTab delivs={delivs} delivByCode={delivByCode} />
        )}
      </div>
    </div>
  );
}


function D2SvgViewer({ deliv, title, testId }) {
  if (!deliv) {
    return <D2EmptyHint message={`${title} — not generated yet.`} />;
  }
  const svgHtml = deliv.content_md || "";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 13, color: "var(--pf-text-2)" }}>
        {title}{" "}
        {deliv.summary && <span style={{ opacity: 0.7 }}>· {deliv.summary}</span>}
      </div>
      <div data-testid={testId}
            style={{ background: "#fafafa", borderRadius: 8,
                      border: "1px solid var(--pf-border)", padding: 14,
                      minHeight: 600 }}
            dangerouslySetInnerHTML={{ __html: svgHtml }} />
    </div>
  );
}


function D2EmptyHint({ message }) {
  return (
    <div data-testid="d2-empty-hint" style={{
      padding: 40, textAlign: "center",
      color: "var(--pf-text-2)",
      background: "var(--pf-surface)",
      border: "1px dashed var(--pf-border)",
      borderRadius: 8,
    }}>
      {message}
    </div>
  );
}


function D2ReportsTab({ delivs, delivByCode }) {
  const reportCodes = ["D.2.1-RPT", "D.2.2-RPT", "D.2.3-RPT"];
  const reports = reportCodes
    .map(c => delivByCode[c])
    .filter(Boolean);
  if (!reports.length) {
    return (
      <D2EmptyHint message="Engineering reports will appear here after D.2 runs." />
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {reports.map(r => (
        <div key={r.code} style={{
          background: "var(--pf-surface)",
          border: "1px solid var(--pf-border)",
          borderRadius: 8, padding: 16,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8,
                         color: "var(--pf-text)" }}>
            {r.code} — {r.name}
            {r.summary && (
              <span style={{ fontWeight: 400, marginLeft: 10,
                              color: "var(--pf-text-2)" }}>
                · {r.summary}
              </span>
            )}
          </div>
          <pre data-testid={`d2-report-${r.code}`} style={{
            margin: 0, fontFamily: "Inter, sans-serif",
            fontSize: 12, lineHeight: 1.6,
            whiteSpace: "pre-wrap", color: "var(--pf-text)",
          }}>{r.content_md || "(empty)"}</pre>
        </div>
      ))}
    </div>
  );
}
